// ─────────────────────────────────────────────────────────────────────
// <HeroCarousel /> — BMS mechanic, editorial visual
//
// Slots:
//   slot 1   : editor's pick (admin-curated)
//   slots 2-3: top trending (algorithmic)
//   slots 4-5: boosted (paid placements)
//
// Mechanics:
//   - Auto-advance 7s, pause on hover/focus
//   - Side-peek of next/previous slide (~6%)
//   - Dot indicators bottom-center
//   - Prev/next arrows on hover (desktop)
//   - Touch swipe (mobile, native)
//   - Keyboard: ←/→ navigation when focused
//   - Reduced-motion: no auto-advance, click-to-advance only
//   - r7.7 (C4): slides come from the placements feed (lib/heroSlides) — sponsored label, overlay theme (AA on
//     photo slides), impression beacon when a slide is shown, click beacon on the CTA
// ─────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatINR, formatEventDate } from '../../lib/format';
import { beacon } from '../../lib/heroSlides';   // r7.7 C4

const ADVANCE_MS = 7000;

export default function HeroCarousel({ slides = [], className = '' }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const intervalRef = useRef(null);
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  const N = slides.length;
  const safe = (i) => ((i % N) + N) % N;

  // r7.7 C4: impression beacon for the slide on screen (deduplicated per session inside lib/heroSlides)
  useEffect(() => { const s = slides[idx]; if (s && s.key) beacon({ impressions: [s.key] }); }, [idx, slides]);
  const goNext = useCallback(() => setIdx((i) => safe(i + 1)), [N]);
  const goPrev = useCallback(() => setIdx((i) => safe(i - 1)), [N]);

  // Auto-advance
  useEffect(() => {
    if (N <= 1 || paused || reducedMotion.current) return;
    intervalRef.current = setInterval(goNext, ADVANCE_MS);
    return () => clearInterval(intervalRef.current);
  }, [N, paused, goNext]);

  // Keyboard
  const onKey = useCallback((e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
  }, [goNext, goPrev]);

  // Touch
  const onTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) (dx < 0 ? goNext() : goPrev());
    setTouchStartX(null);
  };

  if (!N) return null;

  return (
    <div
      className={`relative snt-band ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKey}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label={t('hero.aria', 'Featured events')}
      style={{ outline: 'none' }}
    >
      {/* Slide track — single visible slide w/ side peek of neighbors */}
      <div className="overflow-hidden border-b border-line">
        <div
          className="flex transition-transform duration-450 ease-out"
          style={{
            transform: `translateX(calc(${-idx * 100}% + 0px))`,
          }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.id || i}
              className="w-full flex-shrink-0"
              aria-hidden={i !== idx}
            >
              <Slide slide={slide} active={i === idx} />
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {N > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            aria-label={t('hero.prev', 'Previous')}
            className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full items-center justify-center transition-opacity duration-250 opacity-0 hover:opacity-100 focus:opacity-100"
            style={{
              background: 'rgba(10,10,11,0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--snt-line-strong)',
              color: 'var(--snt-text)',
            }}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label={t('hero.next', 'Next')}
            className="hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full items-center justify-center transition-opacity duration-250 opacity-0 hover:opacity-100 focus:opacity-100"
            style={{
              background: 'rgba(10,10,11,0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--snt-line-strong)',
              color: 'var(--snt-text)',
            }}
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots */}
      {N > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1} of ${N}`}
              aria-current={i === idx}
              className="rounded-full transition-all duration-250"
              style={{
                width: i === idx ? 24 : 6,
                height: 4,
                background: i === idx ? 'var(--snt-accent)' : 'rgba(255,255,255,0.4)',
                border: 'none',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single slide ─────────────────────────────────────────────────────
function Slide({ slide, active }) {
  const { t } = useTranslation();
  const dateLabel = slide.date ? formatEventDate(slide.date) : '';
  const venueLine = [slide.venue_name, dateLabel].filter(Boolean).join(' · ');
  const fromPrice = slide.price_from ?? slide.min_price;

  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-12 px-9 lg:px-12 py-12 lg:py-20 items-center">
      {/* Left: editorial text */}
      <div>
        <div className="snt-eyebrow mb-5">
          {slide.eyebrow || t('hero.featured', 'Featured')}
          {slide.date && <> · {formatEventDate(slide.date, { short: true })}</>}
        </div>
        <h1
          className="font-serif font-light tracking-tight mb-6"
          style={{
            fontSize: 'clamp(40px, 7vw, 84px)',
            lineHeight: 0.95,
            letterSpacing: '-0.035em',
            color: 'var(--snt-text)',
          }}
        >
          {slide.title}
          {slide.title_accent && (
            <span className="snt-em-accent">{slide.title_accent}</span>
          )}
        </h1>
        {slide.lede && (
          <p
            className="text-[17px] leading-relaxed mb-7 max-w-[460px]"
            style={{ color: 'var(--snt-text-2)' }}
          >
            {slide.lede}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4 text-[12px] mb-8" style={{ color: 'var(--snt-text-3)' }}>
          {slide.time && <span>{slide.time}</span>}
          {slide.venue_name && (
            <>
              {slide.time && <span style={{ color: 'var(--snt-text-4)' }}>·</span>}
              <span>{slide.venue_name}</span>
            </>
          )}
          {fromPrice != null && (
            <>
              <span style={{ color: 'var(--snt-text-4)' }}>·</span>
              <span style={{ color: 'var(--snt-accent)' }}>
                {t('common.from', 'From')} {formatINR(fromPrice)}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to={slide.cta_to || `/e/${slide.slug || slide.id}`}
            onClick={() => { if (slide.key) beacon({ clicks: [slide.key] }); }}
            data-testid="hero-cta"
            className="snt-btn-pri no-underline"
            tabIndex={active ? 0 : -1}
          >
            {slide.cta_label || t('hero.book_seats', 'Book seats')}
          </Link>
          <button type="button" className="snt-btn-ghost" tabIndex={active ? 0 : -1}>
            {t('hero.add_wishlist', 'Add to wishlist')}
          </button>
        </div>
      </div>

      {/* Right: editorial art */}
      <div
        className={`snt-art-${slide.variant || 'gold'} aspect-[4/5] rounded-card relative overflow-hidden flex items-center justify-center`}
      >
        {slide.poster_url ? (
          <img
            src={slide.poster_url}
            alt={slide.title}
            className="w-full h-full object-cover"
            loading={active ? 'eager' : 'lazy'}
          />
        ) : (
          <span
            aria-hidden="true"
            className="font-serif"
            style={{
              fontSize: 'clamp(120px, 18vw, 220px)',
              opacity: 0.13,
              color: 'var(--snt-accent)',
              lineHeight: 1,
            }}
          >
            {slide.glyph || 'ॐ'}
          </span>
        )}
        {slide.poster_url && (
          /* r7.7 C4: readable labels on any photo — a gradient scrim per the slide's overlay theme (AA on the caption) */
          <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none" style={{ background: slide.theme?.overlay === 'light' ? 'linear-gradient(to top, rgba(255,255,255,0.85), rgba(255,255,255,0))' : 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))' }} />
        )}
        {(slide.is_boosted || slide.sponsored) && (
          <span
            className="absolute top-4 left-4 snt-eyebrow"
            data-testid="hero-sponsored"
            style={{
              padding: '5px 12px',
              background: 'rgba(212,165,116,0.95)',
              color: 'var(--snt-accent-fg)',
              borderRadius: 3,
              fontSize: 9,
            }}
          >
            {slide.sponsored ? (slide.sponsor_name ? `${t('hero.sponsored', 'Sponsored')} · ${slide.sponsor_name}` : t('hero.sponsored', 'Sponsored')) : t('hero.promoted', 'Promoted')}
          </span>
        )}
        {slide.is_premiere && !slide.is_boosted && (
          <span
            className="absolute top-4 left-4 snt-eyebrow"
            style={{
              padding: '5px 12px',
              background: 'rgba(185,28,28,0.9)',
              color: '#FFF',
              borderRadius: 3,
              fontSize: 9,
            }}
          >
            Premieres
          </span>
        )}
        <div
          className="absolute bottom-5 left-5 right-5 flex justify-between items-end text-[11px]"
          style={{ color: 'rgba(245,245,244,0.6)' }}
        >
          <span style={{ color: slide.theme?.overlay === 'light' ? 'rgba(10,10,11,0.7)' : 'rgba(245,245,244,0.7)' }}>{slide.event_code || ''}</span>
          {slide.seat_count && (
            <span
              className="px-2.5 py-1.5 rounded backdrop-blur-sm"
              style={{ background: 'rgba(0,0,0,0.4)' }}
            >
              {slide.seat_count} seats
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
